import { useDarkMode } from '@/contexts/DarkModeContext';
import { useEditorSettings } from '@/contexts/EditorSettingsContext';
import React, { useCallback } from 'react';
import { styled } from 'styled-components';
import {
  YamlEditor as BaseYamlEditor,
  type YamlEditorDiagnostic,
} from 'ui-components';

interface YamlEditorProps {
  yamlContent: string;
  onYamlContentChange: (yamlContent: string) => void;
  isLoading?: boolean;
  diagnostics?: YamlEditorDiagnostic[];
}

const EditorContainer = styled.div`
  background: ${({ theme }) => theme.colorBgContainer};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  background: ${({ theme }) => theme.colorFillTertiary};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

export const YamlEditor: React.FC<YamlEditorProps> = ({
  yamlContent,
  onYamlContentChange,
  isLoading,
  diagnostics = [],
}) => {
  const [isDarkMode] = useDarkMode();
  const { settings } = useEditorSettings();

  // Handle YAML changes - pass through directly to parent
  const handleYamlChange = useCallback(
    (newYamlContent: string) => {
      onYamlContentChange(newYamlContent);
    },
    [onYamlContentChange],
  );

  if (isLoading) {
    return <LoadingContainer>Loading configuration...</LoadingContainer>;
  }

  return (
    <EditorContainer>
      <BaseYamlEditor
        value={yamlContent}
        onChange={handleYamlChange}
        height="100%"
        placeholder="Enter your pipeline configuration..."
        theme={isDarkMode ? 'dark' : 'light'}
        diagnostics={diagnostics}
        lineWrapping={settings.lineWrapping}
      />
    </EditorContainer>
  );
};
